// src/pages/NotificationsPage.tsx
// ============================================================
// SALT — Notifications Page
//
// Full-page view of all notifications. Primary use case is
// mobile (MobileMenu links here), but also accessible on
// desktop. Uses the same API as the navbar panel.
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import axiosClient from '../api/axiosClient';
import type {
  Notification,
  NotificationsResponse,
  NotificationCategory,
} from '../types/notification.types';
import { getNotificationCategory } from '../types/notification.types';
import { formatRelativeTime } from '../utils/formatters';

// Styles are in notifications.css (imported by Navbar)
// Page-specific styles are inlined below via className conventions

// ── Category icon (same SVGs as NotificationItem) ───────────

function CategoryIcon({ category }: { category: NotificationCategory }) {
  const size = 16;
  const p = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };

  switch (category) {
    case 'team':
      return (<svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>);
    case 'match':
      return (<svg {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /><line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" /></svg>);
    case 'dispute':
      return (<svg {...p}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>);
    case 'rank':
      return (<svg {...p}><path d="M6 9H4.5a2.5 2.5 0 010-5H6" /><path d="M18 9h1.5a2.5 2.5 0 000-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0012 0V2z" /></svg>);
    case 'season':
      return (<svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>);
    case 'admin':
    default:
      return (<svg {...p}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>);
  }
}

// ── Page Component ──────────────────────────────────────────

const LIMIT = 20;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  // Redirect if not logged in
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { authMessage: 'Sign in to view your notifications.' } });
    }
  }, [isAuthenticated, navigate]);

  // Fetch notifications
  const fetchPage = useCallback(async (pageOffset: number) => {
    setIsLoading(true);
    try {
      // API envelope is { success, data: { notifications, total, ... } }
      const res = await axiosClient.get<{ data: NotificationsResponse }>(
        `/notifications?limit=${LIMIT}&offset=${pageOffset}`
      );
      setNotifications(res.data.data.notifications ?? []);
      setTotal(res.data.data.total ?? 0);
      setOffset(pageOffset);
    } catch (err) {
      console.error('[NotificationsPage] fetch failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchPage(0);
  }, [isAuthenticated, fetchPage]);

  // Mark as read + navigate
  const handleClick = async (n: Notification) => {
    if (!n.isRead) {
      try {
        await axiosClient.patch(`/notifications/${n.notificationId}/read`);
        setNotifications((prev) =>
          prev.map((item) =>
            item.notificationId === n.notificationId
              ? { ...item, isRead: true, readAt: new Date().toISOString() }
              : item
          )
        );
      } catch (err) {
        console.error('[NotificationsPage] markAsRead failed:', err);
      }
    }
    if (n.actionUrl) {
      // Handle cross-subdomain navigation via ?gc= param
      const parsed = new URL(n.actionUrl, window.location.origin);
      const gc = parsed.searchParams.get('gc');

      if (gc) {
        parsed.searchParams.delete('gc');
        const cleanPath = parsed.pathname + (parsed.search || '');

        // Validate gameCode against allowlist before redirecting
        const VALID_GAME_CODES = ['hi', 'h3'];
        if (!VALID_GAME_CODES.includes(gc.toLowerCase())) {
          navigate(cleanPath);
          return;
        }

        const host = window.location.hostname.toLowerCase();
        const isOnGameSub =
          /^[a-z0-9]+\.playsalt\.gg$/.test(host) ||
          /^[a-z0-9]+\.localhost/.test(host);

        if (!isOnGameSub) {
          const subBase = process.env.REACT_APP_SUBDOMAIN_BASE || '.playsalt.gg';
          window.location.href = `${window.location.protocol}//${gc.toLowerCase()}${subBase}${cleanPath}`;
          return;
        }
        navigate(cleanPath);
      } else {
        navigate(n.actionUrl);
      }
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await axiosClient.patch('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: n.readAt || new Date().toISOString() }))
      );
    } catch (err) {
      console.error('[NotificationsPage] markAllAsRead failed:', err);
    }
  };

  const hasUnread = notifications.some((n) => !n.isRead);
  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <div className="sl-notif-page">
      <div className="sl-notif-page-inner">
        {/* Header */}
        <div className="sl-notif-page-header">
          <h1 className="sl-notif-page-title">Notifications</h1>
          {hasUnread && (
            <button
              type="button"
              className="sl-notif-mark-all"
              onClick={handleMarkAllRead}
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="sl-notif-skeleton-list">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="sl-notif-skeleton" style={{ padding: '14px 0' }}>
                <div className="sl-notif-skeleton-icon" />
                <div className="sl-notif-skeleton-text">
                  <div className="sl-notif-skeleton-line sl-notif-skeleton-line--title" />
                  <div className="sl-notif-skeleton-line sl-notif-skeleton-line--msg" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="sl-notif-empty" style={{ padding: '48px 16px' }}>
            <svg
              className="sl-notif-empty-icon"
              width="40" height="40" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
            <span className="sl-notif-empty-text">No notifications yet</span>
          </div>
        ) : (
          <>
            <div className="sl-notif-page-list">
              {notifications.map((n) => {
                const category = getNotificationCategory(n.type);
                return (
                  <button
                    key={n.notificationId}
                    type="button"
                    className={`sl-notif-item ${!n.isRead ? 'sl-notif-item--unread' : ''} ${n.actionUrl ? 'sl-notif-item--clickable' : ''}`}
                    onClick={() => handleClick(n)}
                    style={{ borderRadius: 0 }}
                  >
                    <div className={`sl-notif-icon sl-notif-icon--${category}`}>
                      <CategoryIcon category={category} />
                    </div>
                    <div className="sl-notif-content">
                      <div className="sl-notif-title">{n.title}</div>
                      <div className="sl-notif-message">{n.message}</div>
                    </div>
                    <div className="sl-notif-meta">
                      <span className="sl-notif-time">{formatRelativeTime(n.createdAt)}</span>
                      {!n.isRead && <span className="sl-notif-dot" aria-label="Unread" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="sl-notif-page-pagination">
                <button
                  type="button"
                  className="sl-notif-page-btn"
                  disabled={currentPage <= 1}
                  onClick={() => fetchPage(offset - LIMIT)}
                >
                  Previous
                </button>
                <span className="sl-notif-page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="sl-notif-page-btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => fetchPage(offset + LIMIT)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}